import { create } from 'zustand';
import type { PreferredTiming } from '@/types';

// Drives the singleton ChangeSlotModal mounted ONCE at the pickups page level
// (outside the table-row/dropdown DOM tree — mounting the modal inside a row
// caused a stuck ant-zoom-appear animation with pointer-events:none, QA HIGH
// bug). Row-scoped triggers (dropdown item, banner link, rollover "Change")
// call openChangeSlot instead of rendering their own modal.
//
// NOTE: like useSlotSelectionStore, this store is intentionally NOT exported
// from the stores barrel (see the comment in src/stores/index.ts) — import it
// via the direct path '@/stores/useChangeSlotStore'.
export interface ChangeSlotParams {
  uid: string;
  currentCode?: string | null;
  // v1.1: the pickup's currently-saved preferred timing bucket (null =
  // Anytime), so the inline timing block seeds it when the merchant re-picks
  // the same slot in EDIT mode (§6.2). Absent for respond/rollover (defaults to
  // Anytime).
  currentPreferredTiming?: PreferredTiming | null;
  // 'edit' → PATCH pick-ups/{uid}/time-slot; 'respond' → slot-response with
  // choice='change', restricted to remaining same-day slots.
  mode: 'edit' | 'respond';
}

interface ChangeSlotState {
  isOpen: boolean;
  params: ChangeSlotParams | null;
  openChangeSlot: (params: ChangeSlotParams) => void;
  closeChangeSlot: () => void;
}

const useChangeSlotStore = create<ChangeSlotState>((set) => ({
  isOpen: false,
  params: null,
  openChangeSlot: (params: ChangeSlotParams) => set({ isOpen: true, params }),
  closeChangeSlot: () => set({ isOpen: false, params: null }),
}));

export default useChangeSlotStore;
